import React, { useRef } from "react";
import { ThemeData } from "../../api/personalization/theme/data";
import InputItem from "../../components/InputItem";
import ImageUploadInput, { ImageUploadInputRef } from "../../components/ImageUploadInput";
import DropdownControl from "../../components/DropdownControl";
import EditorControl from "../../components/EditorControl";
import { useThemeStore } from "../../api/personalization/theme/store";
import useUXStore from "../../api/ux/store";
import ColorPickerControl from "../../components/ColorPickerControl";

const fontTypes = ["Classic", "Serene", "Modern", "Elegant", "Casual"];

type ThemeViewProps = {
  theme: ThemeData;
  isnew?: boolean;
};

const ThemeView: React.FC<ThemeViewProps> = ({ isnew = false, theme }) => {
  const { updateTheme, deleteTheme, createTheme } = useThemeStore(
    (state) => state
  );

  // Create refs for image upload inputs
  const backgroundImageRef = useRef<ImageUploadInputRef>(null);
  const landingPageImageRef = useRef<ImageUploadInputRef>(null);
  const loginPageImageRef = useRef<ImageUploadInputRef>(null);

  const onApplyEdit = async (newdata: any) => {
    try {
      // Apply all temporary uploads to permanent storage
      const imagePromises = [];

      if (backgroundImageRef.current?.hasTemporaryFile()) {
        imagePromises.push(
          backgroundImageRef.current.apply().then(url => ({
            field: 'BackgroundImageURL',
            url
          }))
        );
      }

      if (landingPageImageRef.current?.hasTemporaryFile()) {
        imagePromises.push(
          landingPageImageRef.current.apply().then(url => ({
            field: 'LandingPageBackgroundURL',
            url
          }))
        );
      }

      if (loginPageImageRef.current?.hasTemporaryFile()) {
        imagePromises.push(
          loginPageImageRef.current.apply().then(url => ({
            field: 'LoginPageBackgroundImageURL',
            url
          }))
        );
      }

      const imageResults = await Promise.all(imagePromises);
      
      // Update data with permanent URLs
      const updatedData = { ...newdata };
      imageResults.forEach(result => {
        if (result && result.url) {
          updatedData[result.field] = result.url;
        }
      });

      // Update the theme
      await updateTheme(updatedData);
      console.log("Theme updated successfully:", updatedData);
    } catch (error) {
      console.error("Error applying changes:", error);
      throw error;
    }
  };

  const onCreateTheme = async (newdata: any) => {
    try {
      // Apply all temporary uploads before creating
      const imagePromises = [];

      if (backgroundImageRef.current?.hasTemporaryFile()) {
        imagePromises.push(
          backgroundImageRef.current.apply().then(url => ({
            field: 'BackgroundImageURL',
            url
          }))
        );
      }

      if (landingPageImageRef.current?.hasTemporaryFile()) {
        imagePromises.push(
          landingPageImageRef.current.apply().then(url => ({
            field: 'LandingPageBackgroundURL',
            url
          }))
        );
      }

      if (loginPageImageRef.current?.hasTemporaryFile()) {
        imagePromises.push(
          loginPageImageRef.current.apply().then(url => ({
            field: 'LoginPageBackgroundImageURL',
            url
          }))
        );
      }

      const imageResults = await Promise.all(imagePromises);
      
      // Update data with permanent URLs
      const dataToCreate = { ...newdata };
      imageResults.forEach(result => {
        if (result && result.url) {
          dataToCreate[result.field] = result.url;
        }
      });

      // Create the theme
      const newId = await createTheme(dataToCreate);
      return newId;
    } catch (error) {
      console.error("Error creating theme:", error);
      throw error;
    }
  };

  const onDeleteTheme = async (data: any) => {
    await deleteTheme(data.Code!);
  };

  const onSelectedFontStyle = (onChanged: (id: string, value: any) => void) => 
    (style: string | null) => {
      onChanged('FontStyle', style!);
    };

  return (
    <EditorControl
      name={theme.Code || "theme"}
      label="theme"
      defaultData={theme}
      isnew={isnew}
      onApply={onApplyEdit}
      onCreate={onCreateTheme}
      onDelete={onDeleteTheme}
    >
      {(editedData, onChanged, showEdit) => {
        const selectedFontStyleI = fontTypes.findIndex(
          (item) => item === editedData.FontStyle
        );

        return (
          <>
            <InputItem
              edit={showEdit}
              id="Code"
              text="Code"
              value={editedData.Code}
              onChanged={onChanged}
            />
            <InputItem
              edit={showEdit}
              id="Description"
              text="Description"
              value={editedData.Description}
              onChanged={onChanged}
            />
            <DropdownControl
              edit={showEdit}
              selected={selectedFontStyleI}
              text="Font Style"
              items={fontTypes}
              onSelected={onSelectedFontStyle(onChanged)}
            />

            <ImageUploadInput
              ref={backgroundImageRef}
              id="BackgroundImageURL"
              text="Background Image"
              value={editedData.BackgroundImageURL}
              permanentPath="themes/background"
              edit={showEdit}
              onChanged={onChanged}
            />

            <ImageUploadInput
              ref={landingPageImageRef}
              id="LandingPageBackgroundURL"
              text="Landing Page Background"
              value={editedData.LandingPageBackgroundURL}
              permanentPath="themes/landing"
              edit={showEdit}
              onChanged={onChanged}
            />

            <ImageUploadInput
              ref={loginPageImageRef}
              id="LoginPageBackgroundImageURL"
              text="Login Background"
              value={editedData.LoginPageBackgroundImageURL}
              permanentPath="themes/login"
              edit={showEdit}
              onChanged={onChanged}
            />

            <ColorPickerControl
              edit={showEdit}
              id="PrimaryAppColor"
              text="Primary Color"
              value={editedData.PrimaryAppColor}
              onChanged={onChanged}
            />
            <ColorPickerControl
              edit={showEdit}
              id="AccentAppColor"
              text="Accent Color"
              value={(editedData.AccentAppColor || "#000000").toLowerCase()}
              onChanged={onChanged}
            />
          </>
        );
      }}
    </EditorControl>
  );
};

export default ThemeView;
